import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Loader2, Shield, TrendingUp, Users } from 'lucide-react';
import { toast } from 'sonner';
import { ShareClassDialog } from './ShareClassDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ShareClass {
  id: string;
  company_id: string;
  name: string;
  class_type: string;
  authorized_shares: number;
  is_preferred: boolean | null;
  liquidation_pref_multiple: number | null;
  par_value: number | null;
  participation: string | null;
  voting_rights: boolean | null;
  created_at: string;
  updated_at: string;
}

interface ShareClassManagementProps {
  companyId: string;
}

export const ShareClassManagement = ({ companyId }: ShareClassManagementProps) => {
  const [shareClasses, setShareClasses] = useState<ShareClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState<ShareClass | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    fetchShareClasses();
  }, [companyId]);

  const fetchShareClasses = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('share_classes')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at');

      if (error) throw error;
      setShareClasses(data || []);
    } catch (error: any) {
      console.error('Error fetching share classes:', error);
      toast.error('Failed to load share classes');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (shareClass: ShareClass) => {
    setSelectedClass(shareClass);
    setDialogOpen(true);
  };

  const handleDelete = (shareClass: ShareClass) => {
    setDeletingId(shareClass.id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingId) return;

    try {
      const { error } = await supabase.from('share_classes').delete().eq('id', deletingId);

      if (error) throw error;

      toast.success('Share class deleted');
      fetchShareClasses();
    } catch (error: any) {
      console.error('Error deleting share class:', error);
      toast.error('Failed to delete share class');
    } finally {
      setDeleteDialogOpen(false);
      setDeletingId(null);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedClass(null);
  };

  const handleSuccess = () => {
    fetchShareClasses();
    handleDialogClose();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Share Classes</CardTitle>
            <CardDescription>
              Manage different classes of shares with specific rights and preferences
            </CardDescription>
          </div>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Class
          </Button>
        </CardHeader>
        <CardContent>
          {shareClasses.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No share classes configured. Add your first share class to get started.
            </div>
          ) : (
            <div className="space-y-4">
              {shareClasses.map((shareClass) => (
                <div
                  key={shareClass.id}
                  className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{shareClass.name}</h3>
                        {shareClass.is_preferred && <Badge variant="secondary">Preferred</Badge>}
                        <Badge variant="outline">{shareClass.class_type}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {shareClass.authorized_shares.toLocaleString()} authorized shares
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(shareClass)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(shareClass)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                    <div className="flex items-start gap-2">
                      <Users className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-xs text-muted-foreground">Voting Rights</p>
                        <p className="text-sm font-medium">
                          {shareClass.voting_rights ? 'Yes' : 'No'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <TrendingUp className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-xs text-muted-foreground">Liquidation Pref</p>
                        <p className="text-sm font-medium">
                          {shareClass.liquidation_pref_multiple
                            ? `${shareClass.liquidation_pref_multiple}x`
                            : 'None'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <Shield className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-xs text-muted-foreground">Participation</p>
                        <p className="text-sm font-medium">{shareClass.participation || 'None'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ShareClassDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        onSuccess={handleSuccess}
        companyId={companyId}
        shareClass={selectedClass}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Share Class</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this share class? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
